import { z } from "zod";
import { Sha256HexSchema } from "./primitives.js";
import { EvalReportSchema } from "./eval.js";
import { RedteamReportSchema } from "./redteam.js";

/** in-toto Statement v1, https://github.com/in-toto/attestation/blob/main/spec/v1/statement.md */
export const InTotoSubjectSchema = z.object({
  name: z.string().min(1),
  digest: z.record(Sha256HexSchema)
});

export const SlsaProvenanceMaterialSchema = z.object({
  uri: z.string().min(1),
  digest: z.record(Sha256HexSchema)
});

export const SlsaProvenancePredicateSchema = z.object({
  builder: z.object({ id: z.string().min(1) }),
  buildType: z.string().min(1),
  invocation: z.object({
    configSource: z
      .object({
        uri: z.string().optional(),
        digest: z.record(Sha256HexSchema).optional(),
        entryPoint: z.string().optional()
      })
      .optional(),
    parameters: z.record(z.unknown()).optional(),
    environment: z.record(z.unknown()).optional()
  }),
  materials: z.array(SlsaProvenanceMaterialSchema).default([])
});

/** CycloneDX 1.6 BOM-ref to the embedded AI-BOM (kept as raw JSON to preserve fidelity). */
export const KeystoneEvidenceSchema = z.object({
  eval: EvalReportSchema,
  redteam: RedteamReportSchema,
  controlsApplied: z.array(z.string().min(1)),
  /** Embedded CycloneDX 1.6 BOM (full document, schemaless here on purpose). */
  cycloneDxBom: z.unknown(),
  /** Compliance crosswalk tags (EU AI Act articles, ISO 42001 controls, etc.). */
  complianceCrosswalk: z
    .object({
      euAiAct: z.array(z.string()).default([]),
      iso42001: z.array(z.string()).default([]),
      nistAiRmf: z.array(z.string()).default([]),
      soc2: z.array(z.string()).default([])
    })
    .default({ euAiAct: [], iso42001: [], nistAiRmf: [], soc2: [] })
});
export type KeystoneEvidence = z.infer<typeof KeystoneEvidenceSchema>;

export const KEYSTONE_PREDICATE_TYPE = "https://keystone.dev/attestation/v1" as const;

export const AttestationPredicateSchema = z.object({
  provenance: SlsaProvenancePredicateSchema,
  evidence: KeystoneEvidenceSchema
});
export type AttestationPredicate = z.infer<typeof AttestationPredicateSchema>;

export const AttestationStatementSchema = z.object({
  _type: z.literal("https://in-toto.io/Statement/v1"),
  subject: z.array(InTotoSubjectSchema).min(1),
  predicateType: z.literal(KEYSTONE_PREDICATE_TYPE),
  predicate: AttestationPredicateSchema
});
export type AttestationStatement = z.infer<typeof AttestationStatementSchema>;

/** DSSE envelope, see https://github.com/secure-systems-lab/dsse/blob/master/envelope.md */
export const DsseSignatureSchema = z.object({
  keyid: z.string().optional(),
  sig: z.string().min(1)
});

export const DsseEnvelopeSchema = z.object({
  payloadType: z.literal("application/vnd.in-toto+json"),
  /** Base64-encoded JSON statement. */
  payload: z.string().min(1),
  signatures: z.array(DsseSignatureSchema).min(1)
});
export type DsseEnvelope = z.infer<typeof DsseEnvelopeSchema>;

export const AttestationBundleSchema = z.object({
  envelope: DsseEnvelopeSchema,
  /** Optional Rekor inclusion proof (production mode). */
  rekor: z
    .object({
      logIndex: z.number().int().nonnegative(),
      logId: z.string().min(1),
      integratedTime: z.number().int().nonnegative(),
      inclusionProof: z.unknown().optional()
    })
    .optional(),
  /** When set to "keystone:dev-only" the bundle is rejected by `verify` unless --allow-dev. */
  trustMode: z.enum(["production", "keystone:dev-only"])
});
export type AttestationBundle = z.infer<typeof AttestationBundleSchema>;
